import React, { FC, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { useDialog } from "../components/dialog/DialogContext";
import TableTab from "../components/tabs/table-tab";
import { TIME_TRIGGERED_ACTIONS } from "../constants/menu-types";
import { useLocale } from "../contexts/localeContext";
import { TableContextProvider } from "../contexts/table/TableContextProvider";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Category } from "../models/Category";
import columns from '../models/ColumnDefinitions/TimeTriggeredActionColumn';
import { Lookup } from "../models/Lookup";
import LookupTypes from "../models/LookupTypes";
import DeviceTimePatternSearch from "../models/search/DeviceTimePatternSearch";
import SiteSearch from "../models/search/SiteSearch";
import searchColumns from '../models/search/TimeTriggeredActionSearch';
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { TimeTriggeredAction } from "../models/TimeTriggeredAction";
import { getDeviceTimePatternNames } from "../services/deviceTimePatternService";
import { getSiteNamesWithPagination } from "../services/siteService";
import { deleteTimeTriggredAction, getTimeTriggeredActionById, getTimeTriggeredActions } from "../services/timeTriggeredActionService";
import { showLoadItemError } from "../services/utilities/ServiceError";
import TableContextStateMonitor from "../contexts/table/TableContextStateMonitor";

interface TimeTriggeredActionTabProps {
    isModalOpen: boolean;
    openModal: (isOpen: boolean) => void;
    setBusy: (isBusy: boolean) => void;
    categories: Category[];
    setCategories: (category: Category[]) => void;
    setCurrentItem: (item: TimeTriggeredAction) => void;
    onFilterChange: (filters: any) => void;
}

const TimeTriggeredActionTab: FC<TimeTriggeredActionTabProps> = ({ setCurrentItem, openModal, setBusy, categories, setCategories, onFilterChange }) => {
    const { translate } = useLocale();
    const title = translate.byKey(TranslationKeys.timeTriggeredActions);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const dialog = useDialog();

    useEffect(() => {
        setLookup([{
            name: 'devAction.deviceAction.deviceImprox.site.id',
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.timePattern,
            values: [],
            dataMethod: getDeviceTimePatternNames,
            searchAccessor: DeviceTimePatternSearch.find(item => item.accessor === "name")?.searchOnName
        }]);
    }, [])

    const editTimeTriggeredAction = async (id: string) => {
        try {
            const timeTriggeredAction = await getTimeTriggeredActionById(id);
            setCurrentItem(timeTriggeredAction)
            openModal(true);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { openModal(false) });
        }
    }

    const onDelete = (id: string): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deleteTimeTriggredAction(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) - 1 }]);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    // user preference items
    const userLoginId = useSelector((state: any) => (state.userInfo.id))

    const initialiseUserPreferences = (setDetails: (userLoginId, TIME_TRIGGERED_ACTIONS) => void) => {
        setDetails(userLoginId, TIME_TRIGGERED_ACTIONS);
    }
    // <-- end context

    return <TableContextProvider
        initialiser={(setDetails) => initialiseUserPreferences(setDetails)}
        dataMethod={getTimeTriggeredActions}
        tableDescriptor={columns}
        searchDescriptor={searchColumns}
        useTable
        usePagination
        useSearch
        useSort
        useFilter
        deleteMethod={onDelete}
        editMethod={editTimeTriggeredAction}
        lookup={lookup}>
        <TableContextStateMonitor onFilterChange={onFilterChange} />
        <TableTab
            title={title}
            selectRows={false} 
            cyName="timeTriggeredActions"/>
    </TableContextProvider>
};

export default TimeTriggeredActionTab;
